import { useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/admin/shared/EmptyState';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { usePatientSessionNotesQuery } from '@/hooks/useSessionNoteQueries';
import { SessionNoteRow } from './SessionNoteRow';

interface SessionNotesTabProps {
  patientId: string;
  isArchived: boolean;
}

const STATUS_FILTERS = [
  { id: null, labelKey: 'sessionNotesList.filterAll' },
  { id: 'draft', labelKey: 'sessionNotesList.filterDraft' },
  { id: 'completed', labelKey: 'sessionNotesList.filterCompleted' },
] as const;

function resolveInitialStatusFilter(requestedStatus: string | null): string | null {
  if (requestedStatus === 'draft' || requestedStatus === 'completed') {
    return requestedStatus;
  }
  return null;
}

export function SessionNotesTab({ patientId, isArchived }: SessionNotesTabProps) {
  const { t } = useTranslation('patients');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(
    resolveInitialStatusFilter(searchParams.get('status'))
  );

  const notesQuery = usePatientSessionNotesQuery(patientId, page, statusFilter);
  const notes = notesQuery.data === undefined ? [] : notesQuery.data.items;
  const totalItems = notesQuery.data === undefined ? 0 : notesQuery.data.totalItems;

  function openNote(noteId: string) {
    navigate(generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, { patientId, noteId }));
  }

  function openNewNote() {
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, { patientId, noteId: 'new' })
    );
  }

  function selectFilter(nextFilter: string | null) {
    setStatusFilter(nextFilter);
    setPage(1);
  }

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-sm font-bold text-zooindigo">
          {t('sessionNotesList.title')}
          {totalItems === 0 ? null : (
            <span className="text-zoomuted font-semibold"> · {totalItems}</span>
          )}
        </h2>
        {isArchived === true ? null : (
          <Button size="chip" onClick={openNewNote}>
            <Icon name="Plus" />
            {t('sessionNotesList.new')}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {STATUS_FILTERS.map(function renderFilterChip(filter) {
          const isActive = filter.id === statusFilter;
          return (
            <Button
              key={filter.id === null ? 'all' : filter.id}
              type="button"
              variant={isActive ? 'primary' : 'outline'}
              size="chip"
              onClick={function selectThisFilter() {
                selectFilter(filter.id);
              }}
            >
              {t(filter.labelKey)}
            </Button>
          );
        })}
      </div>

      {notesQuery.isLoading === true ? (
        <div className="py-8 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : notesQuery.isError === true ? (
        <QueryErrorState
          onRetry={function retry() {
            notesQuery.refetch();
          }}
        />
      ) : notes.length === 0 && statusFilter === null ? (
        <EmptyState
          iconName="FileText"
          title={t('sessionNotesList.emptyTitle')}
          description={t('sessionNotesList.emptyDescription')}
        />
      ) : notes.length === 0 ? (
        <p className="text-sm text-zoomuted py-4">{t('sessionNotesList.emptyFiltered')}</p>
      ) : (
        <div className="space-y-2">
          {notes.map(function renderNote(note) {
            return <SessionNoteRow key={note.id} note={note} onOpen={openNote} />;
          })}
        </div>
      )}

      <ListPagination
        page={page}
        pageSize={ADMIN_LIST_PAGE_SIZE}
        totalItems={totalItems}
        onPageChange={setPage}
      />
    </Card>
  );
}
